import { Link } from '@tanstack/react-router'
import { Droplets, PowerOff, RefreshCw, TriangleAlert } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { DeviceGrid } from './irrigation-device-list'
import { hasSensors, isStale } from './irrigation-sensor-grid'
import type { IrrigationDevice } from '@/lib/api/irrigation-devices-api'
import type { ReactNode } from 'react'

import { PageBackButton } from '@/components/common/PageBackButton'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { useIrrigationDevices } from '@/hooks/use-irrigation-devices-query'
import { ApiError } from '@/lib/api/client'
import { formatApiError } from '@/lib/format-api-error'
import { captureIrrigationSensorsViewed } from '@/lib/irrigation-sensors-tracking'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

/**
 * Màn trạm bơm (bộ phun nước SmartLab) dạng thẻ: mỗi thẻ ghép camera gắn với
 * trạm, trạng thái bơm thiết bị báo về gần nhất và nút bật/tắt; "Cập nhật lúc"
 * là giờ server tưới NHẬN bản ghi, để biết trạng thái còn mới hay đã cũ.
 */
export function IrrigationDevicesScreen() {
    const { hasPermission } = useAuth()
    // Nút bật/tắt tác động bơm thật: chỉ người có quyền control mới thấy.
    const canControl = hasPermission('irrigation.control')
    const {
        data: devices = [],
        isLoading,
        error,
        refetch,
        isFetching,
    } = useIrrigationDevices()
    useSensorsViewedTracking(isLoading || error ? null : devices)

    return (
        <div className="space-y-4 p-4 md:p-6">
            <div className="flex min-w-0 items-start gap-2">
                <PageBackButton className="-my-2 md:-my-1" />
                <div className="min-w-0 flex-1 space-y-1">
                    <h1 className="text-xl font-semibold">
                        {m.irrigation_devices_title()}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {m.irrigation_devices_desc()}
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void refetch()}
                    disabled={isFetching}
                >
                    <RefreshCw
                        className={twJoin(
                            'size-4',
                            isFetching && 'animate-spin',
                        )}
                    />
                    {m.irrigation_devices_refresh()}
                </Button>
            </div>

            {isLoading ? (
                <p className="text-sm text-muted-foreground">
                    {m.common_loading()}
                </p>
            ) : error ? (
                <LoadError error={error} />
            ) : devices.length === 0 ? (
                <StatePanel icon={<Droplets className="size-6" />}>
                    <p className="text-sm font-medium">
                        {m.irrigation_devices_empty()}
                    </p>
                </StatePanel>
            ) : (
                <>
                    <DeviceSummary devices={devices} />
                    <DeviceGrid devices={devices} canControl={canControl} />
                </>
            )}
        </div>
    )
}

function StatePanel({
    icon,
    children,
}: {
    icon: ReactNode
    children: ReactNode
}) {
    return (
        <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed bg-muted/40 px-6 py-16 text-center">
            <div className="text-muted-foreground">{icon}</div>
            {children}
        </div>
    )
}

function LoadError({ error }: { error: Error }) {
    const { hasPermission } = useAuth()
    if (error instanceof ApiError && error.code === 'IRRIGATION_DISABLED') {
        return (
            <StatePanel icon={<PowerOff className="size-6" />}>
                <p className="text-sm font-medium">
                    {m.irrigation_devices_disabled_title()}
                </p>
                <p className="max-w-md text-sm text-muted-foreground">
                    {m.irrigation_devices_disabled_desc()}
                </p>
                {hasPermission('smartlab.read') && (
                    <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="mt-2"
                    >
                        <Link to="/smartlab">
                            {m.irrigation_devices_open_settings()}
                        </Link>
                    </Button>
                )}
            </StatePanel>
        )
    }
    const payload = formatApiError(error, m.irrigation_devices_load_error())
    return (
        <StatePanel icon={<TriangleAlert className="size-6" />}>
            <p className="max-w-md text-sm font-medium">{payload.title}</p>
            {payload.description && (
                <p className="text-xs text-muted-foreground">
                    {payload.description}
                </p>
            )}
        </StatePanel>
    )
}

/**
 * Tóm tắt đầu màn: tổng số bộ tưới và số đang bơm, để biết ngay có trạm nào
 * đang chạy mà không phải lướt từng thẻ.
 */
function DeviceSummary({ devices }: { devices: IrrigationDevice[] }) {
    const running = devices.filter(
        (d) => d.reading_state === 'ok' && d.pump_on === true,
    ).length
    return (
        <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">
                {m.irrigation_devices_count({ count: devices.length })}
            </span>
            {running > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-0.5 font-medium text-primary">
                    <span
                        aria-hidden
                        className="size-2 rounded-full bg-primary motion-safe:animate-pulse"
                    />
                    {m.irrigation_devices_running({ count: running })}
                </span>
            )}
        </div>
    )
}

/**
 * Insights M-137: một event mỗi lần mở màn (không phát lại khi tự làm mới 30
 * giây), đếm bộ tưới có thông số đất / số liệu cũ / chưa đọc được.
 */
function useSensorsViewedTracking(devices: IrrigationDevice[] | null) {
    const sent = useRef(false)
    useEffect(() => {
        if (sent.current || !devices || devices.length === 0) return
        sent.current = true
        captureIrrigationSensorsViewed({
            devices: devices.length,
            withSensors: devices.filter(hasSensors).length,
            stale: devices.filter(isStale).length,
            noReading: devices.filter((d) => d.reading_state !== 'ok').length,
        })
    }, [devices])
}
